'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import {Children, type ReactNode} from 'react';
import {NavArrow, SectionTitle, type NavArrowTone} from '../primitives';
import {carouselArrowSx} from './carouselArrowSx';
import {DetailViewAllLink} from './DetailViewAllLink';
import {useDirectionalScroller} from './useDirectionalScroller';

const CAROUSEL_CHROME_THRESHOLD = 3;

export interface ListingCarouselProps {
  title?: string;
  viewAll?: {label: string; href: string; onClick?: () => void};
  children: ReactNode;
  arrowTone?: NavArrowTone;
}

const Header = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const Viewport = styled(Box)({
  position: 'relative',
});

const Track = styled(Box)(({theme}) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  gap: theme.spacing(4),
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollSnapType: 'x mandatory',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {display: 'none'},
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1.5),
    marginInline: theme.spacing(-2.25),
    paddingInline: theme.spacing(2.25),
    scrollPaddingInline: theme.spacing(2.25),
  },
  '& > *': {
    flex: '0 0 auto',
    scrollSnapAlign: 'start',
    width: 300,
    [theme.breakpoints.up('md')]: {
      width: `calc((100% - ${theme.spacing(8)}) / 3)`,
    },
  },
  // Trailing spacer sized so the native max scroll position lands exactly on
  // the last card's snap-start — without it the browser clamps scrollLeft to
  // `scrollWidth - clientWidth`, which keeps the last card sharing the
  // viewport with its neighbours instead of letting it lead alone.
  '& > [data-carousel-end-spacer]': {
    scrollSnapAlign: 'none',
    width: `calc(100% - 300px - ${theme.spacing(4)})`,
    [theme.breakpoints.down('md')]: {
      width: `calc(100% - 300px - ${theme.spacing(1.5)})`,
    },
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - (100% - ${theme.spacing(8)}) / 3 - ${theme.spacing(4)})`,
    },
  },
}));

export function ListingCarousel({title, viewAll, children, arrowTone = 'light'}: ListingCarouselProps) {
  const count = Children.count(children);
  const showChrome = count > CAROUSEL_CHROME_THRESHOLD;
  const {ref, canPrev, canNext, scrollByDir} = useDirectionalScroller<HTMLDivElement>(count, {fullPageStep: true});

  const showArrows = showChrome && (canPrev || canNext);
  const hasHeader = Boolean(title) || (showChrome && Boolean(viewAll));

  return (
    <Box component="section">
      {hasHeader && (
        <Container>
          <Header>
            {title && <SectionTitle as="h2">{title}</SectionTitle>}
            {showChrome && viewAll && <DetailViewAllLink label={viewAll.label} href={viewAll.href} onClick={viewAll.onClick} />}
          </Header>
        </Container>
      )}
      <Container>
        <Viewport>
          {showArrows && (
            <NavArrow
              direction="prev"
              tone={arrowTone}
              disabled={!canPrev}
              onClick={() => {
                scrollByDir(-1);
              }}
              sx={carouselArrowSx('prev')}
            />
          )}
          <Track ref={ref}>
            {children}
            {showChrome && <Box data-carousel-end-spacer aria-hidden />}
          </Track>
          {showArrows && (
            <NavArrow
              direction="next"
              tone={arrowTone}
              disabled={!canNext}
              onClick={() => {
                scrollByDir(1);
              }}
              sx={carouselArrowSx('next')}
            />
          )}
        </Viewport>
      </Container>
    </Box>
  );
}

ListingCarousel.displayName = 'ListingCarousel';
